import { Size } from "./types";
import { Product, Variant } from "../../types/types";
import { getSizeReductionLabelInfo } from "../../utils/product/getSizeReductionLabelInfo";
import { getVariantDimensions } from "../../utils/product/getVariantDimensions";
import { isInStoreOnly } from "../../utils/variant/isInStoreOnly";
import { createSelectedSizeUrlObj } from "./utils/createSelectedSizeUrl";
import { useRouter } from "next/router";
import { getSizeQuery } from "../../utils/routing/getSizeQuery";
import { useVariant } from "../../hooks/useVariant/useVariant";

export const useAvailableSizes = (
  product: Product,
  options: { sortSizes: boolean },
  showPromoOnIndicators: boolean,
) => {
  const router = useRouter();
  const sizeParam = getSizeQuery(router.query);
  const { variant: selectedVariant } = useVariant(product);
  const productVariants: Variant[] = product.variants;

  const sizesList = productVariants
    .filter((v) => {
      if (selectedVariant.differentiators?.colour) {
        return (
          v.differentiators?.size &&
          v.differentiators?.colour === selectedVariant.differentiators.colour
        );
      } else {
        return v.differentiators?.size;
      }
    })
    .map((sizeVariant, _, sizeVariants) => {
      const isUnavailable =
        !sizeVariant.availability?.availableToOrder &&
        !isInStoreOnly(sizeVariant);

      const { showReduced, label } = getSizeReductionLabelInfo(
        product,
        sizeVariant,
        showPromoOnIndicators,
        isUnavailable,
      );

      return {
        name: sizeVariant.differentiators?.size || "",
        sublabel: getVariantDimensions(sizeVariant),
        sizeReductionLabel: label,
        showReduced: showReduced,
        unavailable: isUnavailable,
        isSelected: isSelected(sizeVariant, sizeParam, sizeVariants.length),
        sizeHeadline: sizeVariant.differentiators?.sizeHeadline || "",
        url: createSelectedSizeUrlObj(sizeVariant, router.query),
      };
    });

  if (options.sortSizes) {
    const isBabyAndToddlerCategory = isWithinBabyAndToddlerCategories(product);
    return sortSizes(sizesList, isBabyAndToddlerCategory);
  }

  return sizesList;
};

export const isSelected = (
  sizeVariant: Variant,
  selectedSize: string | null | undefined,
  sizeOptionsLength: number,
) => {
  const isSingleVariant = sizeOptionsLength === 1;

  if (isSingleVariant) {
    return true;
  }

  if (selectedSize) {
    return sizeVariant.pdpURL.query?.size === selectedSize;
  }

  return false;
};

export const sortSizes = (sizes: Size[], isBabyAndToddler: boolean) => {
  const nonNumericSizes = sizes.filter(({ name }) => isNaN(parseInt(name)));
  const numericSizes = sizes
    .filter(({ name }) => !isNaN(parseInt(name)))
    .sort((a, b) => parseInt(a.name) - parseInt(b.name));
  const sizesSorted = [...nonNumericSizes, ...numericSizes];

  if (isBabyAndToddler) {
    const sizesPremAndOther = sizesSorted.filter(
      (sortedSize) =>
        !sortedSize.name.toLowerCase().includes("tiny baby") &&
        !sortedSize.name.toLowerCase().includes("newborn") &&
        !sortedSize.name.includes("month") &&
        !sortedSize.name.includes("year"),
    );

    const sizeTinyBaby = sizesSorted.filter((sortedSize) =>
      sortedSize.name.toLowerCase().includes("tiny baby"),
    );

    const sizeNewborn = sizesSorted.filter((sortedSize) =>
      sortedSize.name.toLowerCase().includes("newborn"),
    );

    const sizesInMonths = sizesSorted.filter((size) =>
      size.name.includes("month"),
    );

    const sizesInYears = sizesSorted.filter((size) =>
      size.name.includes("year"),
    );

    return [
      ...sizesPremAndOther,
      ...sizeTinyBaby,
      ...sizeNewborn,
      ...sizesInMonths,
      ...sizesInYears,
    ];
  }

  return sizesSorted;
};

export const isWithinBabyAndToddlerCategories = (product: Product) => {
  return product.browsePageHierarchy.some((category) =>
    "url" in category
      ? category.url === "/baby-child/baby-toddlerwear-0-3-yrs/c6000030"
      : false,
  );
};
